/**
 * Chunk Load Error Handler
 *
 * Handles webpack chunk loading errors by implementing retry logic
 * and providing fallback behavior for failed chunk loads.
 */

export interface ChunkRetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  onRetry?: (error: Error, retryCount: number) => void;
  onFailure?: (error: Error) => void;
}

/**
 * Check if an error is a chunk loading error
 */
function isChunkLoadError(error: Error): boolean {
  return (
    !!error &&
    !!error.message &&
    (error.message.indexOf("Loading chunk") !== -1 ||
      error.message.indexOf("Loading CSS chunk") !== -1 ||
      error.name === "ChunkLoadError")
  );
}

/**
 * Retry a failed dynamic import with exponential backoff
 * Note: Simplified version to avoid TypeScript Promise constructor issues
 */
export function retryChunkLoad<T>(
  importFn: () => Promise<T>,
  options: ChunkRetryOptions = {}
): Promise<T> {
  const { maxRetries = 3, retryDelay = 1000, onRetry, onFailure } = options;

  let retryCount = 0;

  function attempt(): Promise<T> {
    return importFn().catch((error: Error) => {
      if (!isChunkLoadError(error) || retryCount >= maxRetries) {
        if (onFailure) {
          onFailure(error);
        }
        throw error;
      }

      retryCount++;

      if (onRetry) {
        onRetry(error, retryCount);
      }

      // Wait for retryDelay milliseconds before re-attempting
      console.warn(
        `Retrying chunk load (attempt ${retryCount}/${maxRetries}) after ${retryDelay}ms:`,
        error.message
      );
      return new Promise<T>(resolve =>
        setTimeout(resolve, retryDelay)
      ).then(() => attempt());
    });
  }

  return attempt();
}

/**
 * Wrap a dynamic import with retry logic
 */
export function withChunkRetry<T>(
  importFn: () => Promise<T>,
  options?: ChunkRetryOptions
): () => Promise<T> {
  return () => retryChunkLoad(importFn, options);
}

/**
 * Set up global chunk loading error handler
 */
export function setupGlobalChunkErrorHandler(): void {
  // Handle unhandled chunk loading errors
  window.addEventListener("unhandledrejection", event => {
    const reason = event.reason;

    // Only process if the rejection reason is an Error instance
    // This prevents issues when non-Error values are rejected
    if (!(reason instanceof Error)) {
      return;
    }

    const error = reason;

    if (isChunkLoadError(error)) {
      console.warn("Chunk loading error detected:", error.message);

      // Try to reload the page as a last resort for chunk errors
      // This helps with cache issues in production
      const shouldReload = !sessionStorage.getItem(
        "chunk-error-reload-attempted"
      );

      if (shouldReload) {
        sessionStorage.setItem("chunk-error-reload-attempted", "true");
        console.warn("Attempting to reload page due to chunk loading error...");

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        // Remove the flag after some time to allow future reloads if needed
        setTimeout(() => {
          sessionStorage.removeItem("chunk-error-reload-attempted");
        }, 300000); // 5 minutes
      }
    }
  });
}
